import {
  type CSSProperties,
  type ForwardedRef,
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactQuill, { Quill, type ReactQuillProps } from "react-quill";
import styled from "styled-components";

import "react-quill/dist/quill.snow.css";

interface ThemeProps {
  sx?: { width?: CSSProperties["width"]; height?: CSSProperties["height"] };
}

interface EditorProps extends Omit<ReactQuillProps, "theme"> {
  editorRef?: ForwardedRef<ReactQuill> | RefObject<ReactQuill>;
  hasImage?: boolean;
  onImageUpload?: (file: File) => Promise<string>;
  error?: boolean;
  resizable?: boolean;
}

const FONT_SIZE_LIST = [
  "8px",
  "9px",
  "10px",
  "12px",
  "14px",
  "16px",
  "20px",
  "24px",
  "28px",
  "32px",
  "36px",
  "40px",
  "48px",
  "60px",
  "72px",
  "96px",
];

const SizeStyle = Quill.import("attributors/style/size");
SizeStyle.whitelist = FONT_SIZE_LIST;

Quill.register(SizeStyle, true);
Quill.register(Quill.import("attributors/style/font"), true);
Quill.register(Quill.import("attributors/style/align"), true);
Quill.register(Quill.import("attributors/style/direction"), true);

const Block = Quill.import("blots/block");
Block.tagName = "div";
Quill.register(Block);

const ReactQuillStyled = styled(ReactQuill)<{
  $customTheme: ThemeProps["sx"];
  $error?: boolean;
}>`
  width: ${({ $customTheme }) => $customTheme?.width ?? "100%"};

  .ql-editor {
    min-height: 150px;

    /* 테이블 스타일 보존 */
    table {
      border-collapse: collapse;
      width: 100%;
      margin: 10px 0;
    }
    td,
    th {
      border: 1px solid #ccc;
      padding: 8px;
    }

    img {
      width: 600px;
      height: auto;
    }
  }

  .ql-toolbar {
    /* MUI Theme 대신 직접 컬러 코드나 props 사용 */
    border: ${({ $error }) =>
      $error ? "1px solid #ff4d4f" : "1px solid #ccc"} !important;
    border-top-left-radius: 0.5rem;
    border-top-right-radius: 0.5rem;
  }

  .ql-container {
    border: ${({ $error }) =>
      $error ? "1px solid #ff4d4f" : "1px solid #ccc"} !important;
    height: ${({ $customTheme }) => $customTheme?.height ?? "auto"};
    font-size: 16px;
    font-family: inherit;
    border-bottom-left-radius: 0.5rem;
    border-bottom-right-radius: 0.5rem;
    background-color: ${({ readOnly }) =>
      readOnly ? "#f5f5f5" : "transparent"};
  }

  /* ✅ 폰트 사이즈 드롭다운 커스터마이징 */
  .ql-snow .ql-picker.ql-size {
    .ql-picker-label::before,
    .ql-picker-item::before {
      content: attr(data-value) !important;
    }
    .ql-picker-label:not([data-value])::before {
      content: "16px" !important;
    }
    .ql-expanded .ql-picker-options {
      max-height: 280px;
      overflow-y: auto;
    }
  }
`;

const ResizableWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const ResizeHandle = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 8px;
  cursor: ns-resize;
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom-left-radius: 0.5rem;
  border-bottom-right-radius: 0.5rem;

  &::after {
    content: "";
    width: 2rem;
    height: 3px;
    border-radius: 2px;
    background-color: #d0d0d0;
  }

  &:hover::after {
    background-color: #999;
  }
`;

/* --- Constants --- */
const TOOLBAR_WITH_IMAGE = [
  [{ font: [] }],
  [{ size: FONT_SIZE_LIST }],
  ["bold", "italic", "underline", "strike"],
  [{ header: 1 }, { header: 2 }, "blockquote", "code-block"],
  [
    { align: [] },
    { direction: "rtl" },
    { list: "ordered" },
    { list: "bullet" },
  ],
  [{ color: [] }, { background: [] }],
  ["link", "image"],
  ["clean"],
];

const TOOLBAR_WITHOUT_IMAGE = [
  [{ font: [] }],
  [{ size: FONT_SIZE_LIST }],
  ["bold", "italic", "underline", "strike"],
  [{ header: 1 }, { header: 2 }],
  ["blockquote", "code-block", "link"],
  [
    { align: [] },
    { direction: "rtl" },
    { list: "ordered" },
    { list: "bullet" },
  ],
  [{ color: [] }, { background: [] }],
  ["clean"],
];

/* --- Main Component --- */
const Editor = ({
  editorRef,
  hasImage,
  onImageUpload,
  sx,
  value,
  onChange,
  resizable,
  error,
  ...props
}: ThemeProps & EditorProps) => {
  const resolvedSx = { height: "20rem", ...sx };
  const onImageUploadRef = useRef(onImageUpload);
  const editorRefRef = useRef(editorRef);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState<number | undefined>(
    undefined,
  );

  useEffect(() => {
    onImageUploadRef.current = onImageUpload;
    editorRefRef.current = editorRef;
  });

  // 데이터 동기화 로직 유지 (Redux 사용 시 필수)
  useEffect(() => {
    if (editorRef && typeof editorRef !== "function" && editorRef.current) {
      const quill = (editorRef.current as any).getEditor();
      if (value !== undefined && value !== quill.root.innerHTML) {
        quill.root.innerHTML = value || "";
      }
    }
  }, [value, editorRef]);

  const handleResizeMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const startY = e.clientY;
    const startHeight =
      wrapperRef.current?.querySelector<HTMLElement>(".ql-container")
        ?.offsetHeight ?? 200;

    const handleMouseMove = (ev: MouseEvent) => {
      const newHeight = Math.max(100, startHeight + (ev.clientY - startY));
      setContainerHeight(newHeight);
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  }, []);

  const modules = useMemo(
    () => ({
      toolbar: {
        container: hasImage ? TOOLBAR_WITH_IMAGE : TOOLBAR_WITHOUT_IMAGE,
        handlers:
          hasImage && onImageUpload
            ? {
                image: function (this: any) {
                  const input = document.createElement("input");
                  input.setAttribute("type", "file");
                  input.setAttribute("accept", "image/*");

                  const currentRef = editorRefRef.current;
                  const quill =
                    currentRef && typeof currentRef !== "function"
                      ? (
                          currentRef as RefObject<ReactQuill>
                        ).current?.getEditor()
                      : null;
                  const savedRange = quill?.getSelection(true);

                  input.click();
                  input.onchange = async () => {
                    const file = input.files?.[0];
                    if (!file || !onImageUploadRef.current || !quill) return;
                    const url = await onImageUploadRef.current(file);
                    const index = savedRange?.index ?? quill.getLength() - 1;
                    quill.insertEmbed(index, "image", url);
                    quill.setSelection(index + 1, 0);
                  };
                },
              }
            : {},
      },
    }),
    [hasImage, onImageUpload],
  );

  return (
    <ResizableWrapper ref={wrapperRef}>
      <ReactQuillStyled
        ref={editorRef as any}
        $customTheme={{
          ...resolvedSx,
          height: containerHeight ? `${containerHeight}px` : resolvedSx.height,
        }}
        $error={error}
        modules={modules}
        value={value || ""}
        onChange={onChange}
        theme="snow"
        {...props}
      />
      {resizable && <ResizeHandle onMouseDown={handleResizeMouseDown} />}
    </ResizableWrapper>
  );
};

export default Editor;
